import { levenshtein } from "./levenshtein";

export interface TimeEntry {
  id: string;
  description: string;
  date: string;
  hours: string;
  minutes: string;
  isbillable: string;
  "person-id": string;
  "person-first-name": string;
  "person-last-name": string;
  "project-id": string;
  "project-name": string;
  "todo-item-id": string;
  "todo-item-name": string;
}

export type Confidence = "HIGH" | "MEDIUM" | "LOW";

export interface DuplicatePair {
  a: TimeEntry;
  b: TimeEntry;
  score: number;
  confidence: Confidence;
  reasons: string[];
}

function totalMinutes(entry: TimeEntry): number {
  return parseInt(entry.hours, 10) * 60 + parseInt(entry.minutes, 10);
}

function stripPrefix(desc: string): string {
  return desc.replace(/^event:\s*/i, "");
}

function scorePair(
  a: TimeEntry,
  b: TimeEntry,
): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  let score = 0;

  const descA = stripPrefix(a.description).toLowerCase();
  const descB = stripPrefix(b.description).toLowerCase();

  if (descA === "" && descB === "") {
    score += 15;
    reasons.push("Both entries have empty descriptions");
  } else {
    const maxLen = Math.max(descA.length, descB.length);
    if (maxLen > 0) {
      const dist = levenshtein(descA, descB);
      const similarity = 1 - dist / maxLen;
      const descScore = similarity * 35;
      score += descScore;
      if (descScore >= 28) {
        reasons.push("Descriptions are nearly identical");
      } else if (descScore >= 18) {
        reasons.push("Descriptions are similar");
      }
    }
  }

  const aHasEvent = /^event:/i.test(a.description);
  const bHasEvent = /^event:/i.test(b.description);
  if (aHasEvent !== bHasEvent) {
    score += 15;
    reasons.push('One entry has an "Event:" prefix and the other does not');
  } else if (aHasEvent && bHasEvent) {
    score += 8;
    reasons.push('Both entries have "Event:" prefix');
  }

  if (a["project-id"] && a["project-id"] === b["project-id"]) {
    score += 15;
    reasons.push("Same project");
  }

  const aTask = a["todo-item-id"];
  const bTask = b["todo-item-id"];
  if (aTask && bTask && aTask === bTask) {
    score += 15;
    reasons.push("Linked to the same task");
  } else if ((aTask && !bTask) || (!aTask && bTask)) {
    score += 10;
    reasons.push("One entry is linked to a task, the other is not");
  }

  const aDate = new Date(a.date);
  const bDate = new Date(b.date);
  if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
    const diffMin = Math.abs(aDate.getTime() - bDate.getTime()) / 60000;
    if (diffMin <= 5) {
      score += 10;
      reasons.push("Start times within 5 minutes");
    } else if (diffMin <= 30) {
      score += 7;
      reasons.push("Start times within 30 minutes");
    } else if (diffMin <= 60) {
      score += 4;
      reasons.push("Start times within 60 minutes");
    }
  }

  const durA = totalMinutes(a);
  const durB = totalMinutes(b);
  const durDiff = Math.abs(durA - durB);
  if (durDiff === 0) {
    score += 10;
    reasons.push("Identical duration");
  } else if (durDiff <= 5) {
    score += 7;
    reasons.push("Duration differs by 5 minutes or less");
  } else if (durDiff <= 15) {
    score += 4;
    reasons.push("Duration differs by 15 minutes or less");
  }

  return { score: Math.min(score, 100), reasons };
}

function confidenceLabel(score: number): Confidence {
  if (score >= 65) return "HIGH";
  if (score >= 40) return "MEDIUM";
  return "LOW";
}

export function detectDuplicates(
  entries: TimeEntry[],
  minScore = 35,
): DuplicatePair[] {
  const byPerson = new Map<string, TimeEntry[]>();
  for (const entry of entries) {
    const pid = entry["person-id"];
    if (!byPerson.has(pid)) byPerson.set(pid, []);
    byPerson.get(pid)!.push(entry);
  }

  const pairs: DuplicatePair[] = [];

  for (const group of byPerson.values()) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const { score, reasons } = scorePair(group[i], group[j]);
        if (score >= minScore) {
          let [left, right] = [group[i], group[j]];
          const leftHasTask =
            left["todo-item-id"] !== "" && left["todo-item-id"] !== "0";
          const rightHasTask =
            right["todo-item-id"] !== "" && right["todo-item-id"] !== "0";
          if (rightHasTask && !leftHasTask) [left, right] = [right, left];
          pairs.push({
            a: left,
            b: right,
            score,
            confidence: confidenceLabel(score),
            reasons,
          });
        }
      }
    }
  }

  return pairs.sort((x, y) => y.score - x.score);
}
